/**
 * useDealFlow — Cross-screen state for the guided selling deal flow.
 *
 * Wraps the app via <DealFlowProvider> in App.jsx (lifted above AppRouter).
 * Holds deal data flowing between screens: lead → business → pitch → plan → proposal → close.
 * Persists key fields to sessionStorage so deal pages survive refresh.
 *
 * Pattern matches CampaignFlowProvider in useMediaPlanFlow.jsx.
 */
import React, {
  createContext,
  useContext,
  useReducer,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { upsertProposalForBusiness } from '@/components/deal/mockDealData';
import { sellerFlowApiClient, SellerFlowApiError } from '@/services/sellerFlowApi';
import {
  activityFromBackend,
  agreementFromBackend,
  businessProfileFromBackend,
  businessProfileFromBusinessRecord,
  businessProfileFromWorkspaceShell,
  businessRecordFromWorkspace,
  campaignFromBackend,
  creativeSetFromCreative,
  creativeFromBackend,
  launchReadinessFromBackend,
  offerConfigFromSelectedPackage,
  pitchBootstrapFromBackend,
  publicProposalErrorStateFromApi,
  publicProposalFromBackend,
  proposalFromBackend,
  proposalPatchFromUi,
  queueLeadFromBackend,
  selectedWorkspaceCreativeFromBackend,
  selectedPackageFromOfferConfig,
  sellerHomeSummaryFromBackend,
} from '@/services/sellerFlowMappers';

// ── Initial state ───────────────────────────────────────────────────────────

const SESSION_KEY = 'cornercast_deal_flow';

function loadPersistedState() {
  try {
    const saved = sessionStorage.getItem(SESSION_KEY);
    if (saved) return JSON.parse(saved);
  } catch {
    // ignore parse errors
  }
  return null;
}

const defaultState = {
  /** Deal identifier */
  dealId: null,
  /** Originating lead ID (if started from queue) */
  leadId: null,
  /** Full lead object */
  lead: null,
  /** Current stage key (maps to Stepper) */
  currentStage: 'discovery',

  // ── V2: Business-centric state ──────────────────────────────────────
  /** Map<businessId, BusinessRecord> — persistent across deals */
  businesses: {},
  /** Currently active business ID */
  activeBusinessId: null,
  /** Map<businessId, CreativeRecord[]> — creative library per business (Phase 2) */
  creatives: {},
  /** Map<businessId, ProposalRecord[]> — proposals per business (Phase 4) */
  proposals: {},
  /** Currently selected creative ID for proposal linking (Phase 6) */
  selectedCreativeId: null,

  // Stage 1: Discovery
  /** Business profile resolved from search */
  businessProfile: null,

  // Stage 2: Scope
  /** { type: 'single_location'|'brand_wide', locations: [], contact?: { name, email } } — contact added at SendProposal */
  scope: null,

  // Stage 3: Pitch
  /** { id, status, objective, tone, offer, format, assets: [], share_url } */
  creativeSet: null,

  // Stage 4: Plan
  /** Selected package template + user customization: { ...template, budget, flight, targetArea, targetAddress } */
  selectedPackage: null,

  // Stage 5: Proposal
  /** { id, status, pitch_message, budget_cap, flight, target_area, price } */
  proposal: null,

  // Stage 6: Send
  /** { method: 'link'|'email', recipients: [], followUpDays } */
  sendConfig: null,

  // Stage 7: Close
  /** { id, accepted_at, billing_mode, payment_method_status, status } */
  agreement: null,

  // Stage 8: Launch — readiness is now derived in LaunchChecklist.jsx (no local state)

  // Stage 9: Live
  /** { id, status, spend, budget_cap, households_reached, impressions } */
  campaign: null,

  /** URL to return to (e.g., work queue) */
  returnTo: null,
};

function getInitialState() {
  const persisted = loadPersistedState();
  if (persisted) {
    return { ...defaultState, ...persisted };
  }
  return { ...defaultState };
}

function getLatestProposal(proposals = []) {
  return [...proposals].sort((a, b) => {
    const versionDiff = (b.version || 0) - (a.version || 0);
    if (versionDiff !== 0) return versionDiff;
    return new Date(b.updated_at || b.created_at || 0).getTime()
      - new Date(a.updated_at || a.created_at || 0).getTime();
  })[0] || null;
}

function upsertCreativeInCollection(creatives = [], nextCreative) {
  if (!nextCreative?.id) return creatives;

  const index = creatives.findIndex((creative) => creative.id === nextCreative.id);
  const next = [...creatives];

  if (index >= 0) {
    next[index] = { ...next[index], ...nextCreative };
  } else {
    next.unshift(nextCreative);
  }

  return sortCreativeCollection(next);
}

function getCreativeStatusPriority(creative = {}) {
  switch (creative.status) {
    case 'processing':
      return 0;
    case 'failed':
      return 1;
    case 'rejected':
      return 2;
    case 'ready':
    case 'approved':
      return 3;
    case 'draft':
      return 4;
    case 'archived':
      return 5;
    default:
      return 6;
  }
}

function getCreativeTimestamp(creative = {}) {
  return new Date(creative.updatedAt || creative.createdAt || 0).getTime();
}

function sortCreativeCollection(creatives = []) {
  return [...creatives].sort((a, b) => {
    const priorityDiff = getCreativeStatusPriority(a) - getCreativeStatusPriority(b);
    if (priorityDiff !== 0) return priorityDiff;
    return getCreativeTimestamp(b) - getCreativeTimestamp(a);
  });
}

function buildCreativeCollection({
  workspace,
  businessRecord,
  items = [],
}) {
  const listedCreatives = items.map((item) =>
    creativeFromBackend(item, workspace?.selected_creative_binding_id),
  );

  const selectedCreativeFallback = selectedWorkspaceCreativeFromBackend(workspace?.selected_creative, {
    businessId: businessRecord?.id || null,
    businessName: businessRecord?.name || 'Business',
  });

  const collection = selectedCreativeFallback && !listedCreatives.some((creative) => creative.id === selectedCreativeFallback.id)
    ? [selectedCreativeFallback, ...listedCreatives]
    : listedCreatives;

  return sortCreativeCollection(collection);
}

function syncCreativeSelectionFlags(creatives = [], selectedBindingId = null) {
  return sortCreativeCollection(creatives.map((item) => ({
    ...item,
    isSelected: item.bindingId === selectedBindingId || item.id === selectedBindingId,
    selectedOnWorkspace: item.bindingId === selectedBindingId || item.id === selectedBindingId,
  })));
}

// ── Reducer ─────────────────────────────────────────────────────────────────

function dealReducer(state, action) {
  switch (action.type) {
    case 'SET_DEAL_ID':
      return { ...state, dealId: action.payload };
    case 'SET_LEAD_ID':
      return { ...state, leadId: action.payload };
    case 'SET_LEAD':
      return { ...state, lead: action.payload };
    case 'SET_CURRENT_STAGE':
      return { ...state, currentStage: action.payload };
    case 'SET_BUSINESS_PROFILE':
      return { ...state, businessProfile: action.payload };
    case 'SET_SCOPE':
      return { ...state, scope: action.payload };
    case 'SET_CREATIVE_SET':
      return { ...state, creativeSet: action.payload };
    case 'SET_SELECTED_PACKAGE':
      return { ...state, selectedPackage: action.payload };
    case 'SET_PROPOSAL':
      return { ...state, proposal: action.payload };
    case 'SET_SEND_CONFIG':
      return { ...state, sendConfig: action.payload };
    case 'SET_AGREEMENT':
      return { ...state, agreement: action.payload };
    case 'SET_CAMPAIGN':
      return { ...state, campaign: action.payload };
    case 'SET_RETURN_TO':
      return { ...state, returnTo: action.payload };

    // ── V2: Business-centric actions ──────────────────────────────────
    case 'ADD_BUSINESS': {
      const biz = action.payload;
      return {
        ...state,
        businesses: { ...state.businesses, [biz.id]: biz },
        activeBusinessId: biz.id,
      };
    }
    case 'UPSERT_BUSINESS': {
      const biz = action.payload;
      const existing = state.businesses[biz.id] || {};
      return {
        ...state,
        businesses: {
          ...state.businesses,
          [biz.id]: { ...existing, ...biz },
        },
      };
    }
    case 'SET_ACTIVE_BUSINESS_ID':
      return { ...state, activeBusinessId: action.payload };

    // Creative library (Phase 2)
    case 'ADD_CREATIVE': {
      const { businessId, creative } = action.payload;
      const existing = state.creatives[businessId] || [];
      return {
        ...state,
        creatives: { ...state.creatives, [businessId]: [...existing, creative] },
      };
    }
    case 'SET_CREATIVES_FOR_BUSINESS': {
      const { businessId, creatives } = action.payload;
      return {
        ...state,
        creatives: { ...state.creatives, [businessId]: creatives },
      };
    }
    case 'UPDATE_CREATIVE_STATUS': {
      const { businessId: bizId, creativeId, status } = action.payload;
      const list = (state.creatives[bizId] || []).map((c) =>
        c.id === creativeId ? { ...c, status } : c
      );
      return { ...state, creatives: { ...state.creatives, [bizId]: list } };
    }

    // Proposals (Phase 4)
    case 'ADD_PROPOSAL_TO_BUSINESS': {
      const { businessId: bId, proposal: prop } = action.payload;
      const existingProps = state.proposals[bId] || [];
      return {
        ...state,
        proposals: { ...state.proposals, [bId]: [...existingProps, prop] },
      };
    }
    case 'SET_PROPOSALS_FOR_BUSINESS': {
      const { businessId: bId, proposals } = action.payload;
      return {
        ...state,
        proposals: { ...state.proposals, [bId]: proposals },
      };
    }
    case 'UPSERT_PROPOSAL_TO_BUSINESS': {
      const { businessId: bId, proposal: prop } = action.payload;
      return {
        ...state,
        proposals: upsertProposalForBusiness(state.proposals, bId, prop),
      };
    }
    case 'UPDATE_PROPOSAL_STATUS': {
      const { businessId: pBizId, proposalId, status: pStatus } = action.payload;
      const propList = (state.proposals[pBizId] || []).map((p) =>
        p.id === proposalId ? { ...p, status: pStatus, updated_at: new Date().toISOString() } : p
      );
      return { ...state, proposals: { ...state.proposals, [pBizId]: propList } };
    }
    case 'UPDATE_AGREEMENT':
      return { ...state, agreement: { ...(state.agreement || {}), ...action.payload } };

    // Creative-Proposal linking (Phase 6)
    case 'SET_SELECTED_CREATIVE':
      return { ...state, selectedCreativeId: action.payload };

    // Generic creative update (Phase 11)
    case 'UPDATE_CREATIVE': {
      const { businessId: ucBizId, creativeId: ucCreativeId, updates: ucUpdates } = action.payload;
      const ucList = (state.creatives[ucBizId] || []).map((c) =>
        c.id === ucCreativeId ? { ...c, ...ucUpdates } : c
      );
      return { ...state, creatives: { ...state.creatives, [ucBizId]: ucList } };
    }
    // Generic proposal update (Phase 11)
    case 'UPDATE_PROPOSAL': {
      const { businessId: upBizId, proposalId: upPropId, updates: upUpdates } = action.payload;
      const upList = (state.proposals[upBizId] || []).map((p) =>
        p.id === upPropId ? { ...p, ...upUpdates, updated_at: upUpdates.updated_at || new Date().toISOString() } : p
      );
      return { ...state, proposals: { ...state.proposals, [upBizId]: upList } };
    }

    case 'LINK_PROPOSAL_TO_CREATIVE': {
      const { businessId: lBizId, creativeId: lCreativeId, proposalId: lProposalId } = action.payload;
      const creativeList = (state.creatives[lBizId] || []).map((c) => {
        if (c.id === lCreativeId) {
          const existing = c.proposalIds || [];
          if (existing.includes(lProposalId)) return c;
          return { ...c, proposalIds: [...existing, lProposalId] };
        }
        return c;
      });
      return { ...state, creatives: { ...state.creatives, [lBizId]: creativeList } };
    }

    case 'RESET':
      return { ...defaultState };
    default:
      return state;
  }
}

// ── Context ─────────────────────────────────────────────────────────────────

const DealFlowContext = createContext(null);

export function DealFlowProvider({ children }) {
  const [state, dispatch] = useReducer(dealReducer, null, getInitialState);
  const [sellerHomeSummary, setSellerHomeSummaryState] = useState(null);
  const [sellerHomeQueue, setSellerHomeQueueState] = useState([]);
  const [sellerHomeLoading, setSellerHomeLoading] = useState(false);
  const [workspaceActivityByBusiness, setWorkspaceActivityByBusiness] = useState({});
  const [launchReadinessByBusiness, setLaunchReadinessByBusiness] = useState({});
  const [campaignsByBusiness, setCampaignsByBusiness] = useState({});
  const [reportSummaryByBusiness, setReportSummaryByBusiness] = useState({});
  const [latestReportSnapshotByBusiness, setLatestReportSnapshotByBusiness] = useState({});
  const [reportSnapshotHistoryByCampaign, setReportSnapshotHistoryByCampaign] = useState({});
  const [businessProfilesByBusiness, setBusinessProfilesByBusiness] = useState({});
  const [businessProfileLoadingByBusiness, setBusinessProfileLoadingByBusiness] = useState({});
  const [businessProfileErrorByBusiness, setBusinessProfileErrorByBusiness] = useState({});
  const [workspaceLoadingByBusiness, setWorkspaceLoadingByBusiness] = useState({});
  const [workspaceErrorByBusiness, setWorkspaceErrorByBusiness] = useState({});
  const businessesRef = useRef(state.businesses);
  const creativesRef = useRef(state.creatives);
  const proposalsRef = useRef(state.proposals);
  const selectedCreativeIdRef = useRef(state.selectedCreativeId);
  const creativeSetRef = useRef(state.creativeSet);
  const businessProfileRef = useRef(state.businessProfile);
  const activeBusinessIdRef = useRef(state.activeBusinessId);

  useEffect(() => {
    businessesRef.current = state.businesses;
  }, [state.businesses]);

  useEffect(() => {
    creativesRef.current = state.creatives;
  }, [state.creatives]);

  useEffect(() => {
    proposalsRef.current = state.proposals;
  }, [state.proposals]);

  useEffect(() => {
    selectedCreativeIdRef.current = state.selectedCreativeId;
  }, [state.selectedCreativeId]);

  useEffect(() => {
    creativeSetRef.current = state.creativeSet;
  }, [state.creativeSet]);

  useEffect(() => {
    businessProfileRef.current = state.businessProfile;
  }, [state.businessProfile]);

  useEffect(() => {
    activeBusinessIdRef.current = state.activeBusinessId;
  }, [state.activeBusinessId]);

  // Persist key fields to sessionStorage on every change
  useEffect(() => {
    try {
      const toPersist = {
        dealId: state.dealId,
        leadId: state.leadId,
        currentStage: state.currentStage,
        returnTo: state.returnTo,
        // Persist lightweight business profile reference
        businessProfile: state.businessProfile
          ? { id: state.businessProfile.id, name: state.businessProfile.name }
          : null,
        // V2: Persist business records and active business
        businesses: state.businesses,
        activeBusinessId: state.activeBusinessId,
        // V2: Persist creative library (lightweight records)
        creatives: state.creatives,
        // V2: Persist selected package (survives multi-screen offer flow)
        selectedPackage: state.selectedPackage,
        // V2: Persist proposals map (lightweight records)
        proposals: state.proposals,
        // V2: Persist selected creative for proposal linking
        selectedCreativeId: state.selectedCreativeId,
        agreement: state.agreement,
        sendConfig: state.sendConfig,
        proposal: state.proposal,
        campaign: state.campaign,
        // Don't persist large objects (lead, creativeSet)
      };
      sessionStorage.setItem(SESSION_KEY, JSON.stringify(toPersist));
    } catch {
      // Storage full or unavailable — fail silently
    }
  }, [state.dealId, state.leadId, state.currentStage, state.returnTo, state.businessProfile, state.businesses, state.activeBusinessId, state.creatives, state.selectedPackage, state.proposals, state.selectedCreativeId, state.agreement, state.sendConfig, state.proposal, state.campaign]);

  // ── Actions ─────────────────────────────────────────────────────────────

  const setDealId = useCallback((id) => {
    dispatch({ type: 'SET_DEAL_ID', payload: id });
  }, []);

  const setLeadId = useCallback((id) => {
    dispatch({ type: 'SET_LEAD_ID', payload: id });
  }, []);

  const setLead = useCallback((lead) => {
    dispatch({ type: 'SET_LEAD', payload: lead });
  }, []);

  const setCurrentStage = useCallback((stage) => {
    dispatch({ type: 'SET_CURRENT_STAGE', payload: stage });
  }, []);

  const setBusinessProfile = useCallback((bp) => {
    dispatch({ type: 'SET_BUSINESS_PROFILE', payload: bp });
  }, []);

  const setScope = useCallback((scope) => {
    dispatch({ type: 'SET_SCOPE', payload: scope });
  }, []);

  const setCreativeSet = useCallback((cs) => {
    dispatch({ type: 'SET_CREATIVE_SET', payload: cs });
  }, []);

  const setSelectedPackage = useCallback((pkg) => {
    dispatch({ type: 'SET_SELECTED_PACKAGE', payload: pkg });
  }, []);

  const setProposal = useCallback((p) => {
    dispatch({ type: 'SET_PROPOSAL', payload: p });
  }, []);

  const setSendConfig = useCallback((sc) => {
    dispatch({ type: 'SET_SEND_CONFIG', payload: sc });
  }, []);

  const setAgreement = useCallback((a) => {
    dispatch({ type: 'SET_AGREEMENT', payload: a });
  }, []);

  const setCampaign = useCallback((c) => {
    dispatch({ type: 'SET_CAMPAIGN', payload: c });
  }, []);

  const setReturnTo = useCallback((path) => {
    dispatch({ type: 'SET_RETURN_TO', payload: path });
  }, []);

  const reset = useCallback(() => {
    sessionStorage.removeItem(SESSION_KEY);
    setSellerHomeSummaryState(null);
    setSellerHomeQueueState([]);
    setSellerHomeLoading(false);
    setWorkspaceActivityByBusiness({});
    setLaunchReadinessByBusiness({});
    setCampaignsByBusiness({});
    setReportSummaryByBusiness({});
    setLatestReportSnapshotByBusiness({});
    setReportSnapshotHistoryByCampaign({});
    setBusinessProfilesByBusiness({});
    setBusinessProfileLoadingByBusiness({});
    setBusinessProfileErrorByBusiness({});
    setWorkspaceLoadingByBusiness({});
    setWorkspaceErrorByBusiness({});
    dispatch({ type: 'RESET' });
  }, []);

  // ── V2: Business-centric actions ────────────────────────────────────

  const addBusiness = useCallback((business) => {
    dispatch({ type: 'ADD_BUSINESS', payload: business });
  }, []);

  const upsertBusiness = useCallback((business) => {
    dispatch({ type: 'UPSERT_BUSINESS', payload: business });
  }, []);

  const setActiveBusinessId = useCallback((id) => {
    dispatch({ type: 'SET_ACTIVE_BUSINESS_ID', payload: id });
  }, []);

  const addCreative = useCallback((businessId, creative) => {
    dispatch({ type: 'ADD_CREATIVE', payload: { businessId, creative } });
  }, []);

  const setCreativesForBusiness = useCallback((businessId, creatives) => {
    dispatch({ type: 'SET_CREATIVES_FOR_BUSINESS', payload: { businessId, creatives } });
  }, []);

  const updateCreativeStatus = useCallback((businessId, creativeId, status) => {
    dispatch({ type: 'UPDATE_CREATIVE_STATUS', payload: { businessId, creativeId, status } });
  }, []);

  const addProposalToBusiness = useCallback((businessId, proposal) => {
    dispatch({ type: 'ADD_PROPOSAL_TO_BUSINESS', payload: { businessId, proposal } });
  }, []);

  const setProposalsForBusiness = useCallback((businessId, proposals) => {
    dispatch({ type: 'SET_PROPOSALS_FOR_BUSINESS', payload: { businessId, proposals } });
  }, []);

  const upsertProposalToBusiness = useCallback((businessId, proposal) => {
    dispatch({ type: 'UPSERT_PROPOSAL_TO_BUSINESS', payload: { businessId, proposal } });
  }, []);

  const updateProposalStatus = useCallback((businessId, proposalId, status) => {
    dispatch({ type: 'UPDATE_PROPOSAL_STATUS', payload: { businessId, proposalId, status } });
  }, []);

  const setSelectedCreative = useCallback((creativeId) => {
    dispatch({ type: 'SET_SELECTED_CREATIVE', payload: creativeId });
  }, []);

  const updateAgreement = useCallback((updates) => {
    dispatch({ type: 'UPDATE_AGREEMENT', payload: updates });
  }, []);

  const updateCreative = useCallback((businessId, creativeId, updates) => {
    dispatch({ type: 'UPDATE_CREATIVE', payload: { businessId, creativeId, updates } });
  }, []);

  const updateProposal = useCallback((businessId, proposalId, updates) => {
    dispatch({ type: 'UPDATE_PROPOSAL', payload: { businessId, proposalId, updates } });
  }, []);

  const linkProposalToCreative = useCallback((businessId, creativeId, proposalId) => {
    dispatch({ type: 'LINK_PROPOSAL_TO_CREATIVE', payload: { businessId, creativeId, proposalId } });
  }, []);

  const getWorkspaceIdForBusiness = useCallback(
    (businessId) => businessesRef.current?.[businessId]?.workspaceId || null,
    [],
  );

  const optionalRequest = useCallback(async (request) => {
    try {
      return await request();
    } catch (error) {
      if (error instanceof SellerFlowApiError && error.isNotFound) {
        return null;
      }
      throw error;
    }
  }, []);

  const setWorkspaceLoadingState = useCallback((businessId, loading) => {
    setWorkspaceLoadingByBusiness((prev) => ({ ...prev, [businessId]: loading }));
  }, []);

  const setWorkspaceErrorState = useCallback((businessId, error) => {
    setWorkspaceErrorByBusiness((prev) => ({ ...prev, [businessId]: error || null }));
  }, []);

  const setBusinessProfileLoadingState = useCallback((businessId, loading) => {
    setBusinessProfileLoadingByBusiness((prev) => ({ ...prev, [businessId]: loading }));
  }, []);

  const setBusinessProfileErrorState = useCallback((businessId, error) => {
    setBusinessProfileErrorByBusiness((prev) => ({ ...prev, [businessId]: error || null }));
  }, []);

  const hydrateBusinessProfileForBusiness = useCallback((businessId, profile, {
    setActive = false,
    existingBusiness = null,
  } = {}) => {
    if (!businessId || !profile) return null;

    setBusinessProfilesByBusiness((prev) => ({ ...prev, [businessId]: profile }));

    const baseBusiness = existingBusiness || businessesRef.current?.[businessId] || {};
    const businessSummary = profile.business || {};
    const address = businessSummary.address || profile.address || null;
    const name = businessSummary.name || profile.name || null;
    const category = profile.category || null;
    const websiteUrl = businessSummary.website_url || profile.website_url || null;
    const rating = businessSummary.stars ?? profile.rating ?? null;
    const reviews = businessSummary.review_count ?? profile.reviews ?? null;

    upsertBusiness({
      ...baseBusiness,
      id: businessId,
      advertiserId: profile.advertiserId || businessId,
      workspaceId: profile.workspaceId || baseBusiness.workspaceId || null,
      organizationId: profile.organizationId || baseBusiness.organizationId || null,
      name: name || baseBusiness.name || 'Business',
      address: address || baseBusiness.address || '',
      category: category || baseBusiness.category || null,
      rating,
      reviews,
      website_url: websiteUrl,
    });

    if (setActive) {
      setBusinessProfile(profile);
    }

    return profile;
  }, [setBusinessProfile, upsertBusiness]);

  const loadBusinessProfileForBusiness = useCallback(async (
    businessId,
    {
      advertiserId = null,
      workspaceId = null,
      workspace = null,
      businessRecord = null,
      setActive = false,
    } = {},
  ) => {
    if (!businessId && !advertiserId && !workspaceId) return null;

    const resolvedBusinessId = businessId || advertiserId;
    const resolvedBusiness = businessRecord || businessesRef.current?.[resolvedBusinessId] || null;
    const resolvedAdvertiserId = advertiserId || resolvedBusiness?.advertiserId || businessId;
    const resolvedWorkspaceId = workspaceId || workspace?.id || resolvedBusiness?.workspaceId || null;

    setBusinessProfileLoadingState(resolvedBusinessId, true);
    setBusinessProfileErrorState(resolvedBusinessId, null);

    try {
      let response = null;

      if (resolvedWorkspaceId) {
        response = await optionalRequest(() => sellerFlowApiClient.getWorkspaceBusinessProfile(resolvedWorkspaceId));
      }

      if (!response && resolvedAdvertiserId) {
        response = await optionalRequest(() => sellerFlowApiClient.getAdvertiserBusinessProfile(resolvedAdvertiserId));
      }

      const profile = response
        ? businessProfileFromBackend(response, {
          id: resolvedBusinessId,
          workspaceId: resolvedWorkspaceId,
          organizationId: resolvedBusiness?.organizationId || null,
          business: workspace?.business || null,
        })
        : workspace?.business_profile
          ? businessProfileFromWorkspaceShell(workspace, resolvedBusiness)
          : null;

      if (!profile) {
        return null;
      }

      return hydrateBusinessProfileForBusiness(resolvedBusinessId, profile, {
        setActive,
        existingBusiness: resolvedBusiness,
      });
    } catch (error) {
      setBusinessProfileErrorState(resolvedBusinessId, error);
      throw error;
    } finally {
      setBusinessProfileLoadingState(resolvedBusinessId, false);
    }
  }, [
    hydrateBusinessProfileForBusiness,
    optionalRequest,
    setBusinessProfileErrorState,
    setBusinessProfileLoadingState,
  ]);

  const refreshBusinessProfileForBusiness = useCallback(async (businessId, options = {}) => (
    loadBusinessProfileForBusiness(businessId, options)
  ), [loadBusinessProfileForBusiness]);

  const enrichBusinessProfileForBusiness = useCallback(async (
    businessId,
    payload = null,
    {
      advertiserId = null,
      workspaceId = null,
      workspace = null,
      businessRecord = null,
      setActive = false,
    } = {},
  ) => {
    const resolvedBusinessId = businessId || advertiserId;
    const resolvedBusiness = businessRecord || businessesRef.current?.[resolvedBusinessId] || null;
    const resolvedAdvertiserId = advertiserId || resolvedBusiness?.advertiserId || businessId;
    const resolvedWorkspaceId = workspaceId || workspace?.id || resolvedBusiness?.workspaceId || null;
    const originPayload = payload || resolvedBusiness?.origin || workspace?.origin_json || null;

    setBusinessProfileLoadingState(resolvedBusinessId, true);
    setBusinessProfileErrorState(resolvedBusinessId, null);

    try {
      const response = resolvedWorkspaceId
        ? await sellerFlowApiClient.enrichWorkspaceBusinessProfile(resolvedWorkspaceId, originPayload)
        : await sellerFlowApiClient.enrichAdvertiserBusinessProfile(resolvedAdvertiserId, originPayload);

      const profile = businessProfileFromBackend(response, {
        id: resolvedBusinessId,
        workspaceId: resolvedWorkspaceId,
        organizationId: resolvedBusiness?.organizationId || null,
      });

      return hydrateBusinessProfileForBusiness(resolvedBusinessId, profile, {
        setActive,
        existingBusiness: resolvedBusiness,
      });
    } catch (error) {
      setBusinessProfileErrorState(resolvedBusinessId, error);
      throw error;
    } finally {
      setBusinessProfileLoadingState(resolvedBusinessId, false);
    }
  }, [
    hydrateBusinessProfileForBusiness,
    setBusinessProfileErrorState,
    setBusinessProfileLoadingState,
  ]);

  const loadSellerHome = useCallback(async (filters = {}) => {
    setSellerHomeLoading(true);
    try {
      const [summaryResponse, queueResponse] = await Promise.all([
        sellerFlowApiClient.getSellerHomeSummary(),
        sellerFlowApiClient.getSellerHomeQueue(filters),
      ]);

      const summary = sellerHomeSummaryFromBackend(summaryResponse);
      const queue = (queueResponse?.items || []).map(queueLeadFromBackend);

      queue.forEach((item) => {
        upsertBusiness({
          id: item.advertiserId,
          advertiserId: item.advertiserId,
          workspaceId: item.workspaceId,
          organizationId: item.organization_id || null,
          name: item.businessName,
          address: item.businessAddress,
          category: item.businessCategory,
          rating: item.businessRating,
          reviews: item.businessReviewsCount,
        });
      });

      setSellerHomeSummaryState(summary);
      setSellerHomeQueueState(queue);
      return { summary, queue };
    } finally {
      setSellerHomeLoading(false);
    }
  }, [upsertBusiness]);

  const loadWorkspaceBundle = useCallback(async (
    businessId,
    { setActive = true, ensure = false, originJson = null } = {},
  ) => {
    if (!businessId) return null;

    setWorkspaceLoadingState(businessId, true);
    setWorkspaceErrorState(businessId, null);

    try {
      let workspace = null;
      try {
        workspace = ensure
          ? await sellerFlowApiClient.ensureWorkspace(businessId, originJson)
          : await sellerFlowApiClient.getActiveWorkspace(businessId);
      } catch (error) {
        if (error instanceof SellerFlowApiError && error.isNotFound) {
          workspace = await sellerFlowApiClient.ensureWorkspace(businessId, originJson);
        } else {
          throw error;
        }
      }

      const businessRecord = businessRecordFromWorkspace(workspace);
      upsertBusiness(businessRecord);
      const shellBusinessProfile = workspace?.business_profile
        ? businessProfileFromWorkspaceShell(workspace, businessRecord)
        : businessProfileFromBusinessRecord(businessRecord);

      if (setActive) {
        setActiveBusinessId(businessRecord.id);
        setBusinessProfile(shellBusinessProfile);
      }

      const workspaceId = workspace.id;

      const [
        businessProfileResponse,
        activityResponse,
        offerResponse,
        proposalsResponse,
        agreementResponse,
        readinessResponse,
        campaignResponse,
        workspaceReportSummaryResponse,
      ] = await Promise.all([
        optionalRequest(() => sellerFlowApiClient.getWorkspaceBusinessProfile(workspaceId)),
        optionalRequest(() => sellerFlowApiClient.getWorkspaceActivity(workspaceId)),
        optionalRequest(() => sellerFlowApiClient.getOfferConfig(workspaceId)),
        optionalRequest(() => sellerFlowApiClient.listProposals(workspaceId)),
        optionalRequest(() => sellerFlowApiClient.getWorkspaceAgreement(workspaceId)),
        optionalRequest(() => sellerFlowApiClient.getLaunchReadiness(workspaceId)),
        optionalRequest(() => sellerFlowApiClient.getWorkspaceCampaign(workspaceId)),
        optionalRequest(() => sellerFlowApiClient.getWorkspaceCampaignReportSummary(workspaceId)),
      ]);

      let latestReportSnapshot = null;
      let reportSnapshotHistory = [];
      if (campaignResponse?.id) {
        const [latestSnapshotResponse, historyResponse] = await Promise.all([
          optionalRequest(() => sellerFlowApiClient.getLatestReportSnapshot(campaignResponse.id)),
          optionalRequest(() => sellerFlowApiClient.listReportSnapshots(campaignResponse.id)),
        ]);
        latestReportSnapshot = latestSnapshotResponse;
        reportSnapshotHistory = historyResponse?.items || [];
      }

      let creatives = [];
      try {
        const workspaceCreativeResponse = await optionalRequest(() => sellerFlowApiClient.getWorkspaceCreatives(workspaceId));
        if (workspaceCreativeResponse?.items?.length) {
          creatives = buildCreativeCollection({
            workspace,
            businessRecord,
            items: workspaceCreativeResponse.items,
          });
        } else {
          const advertiserCreativeResponse = await optionalRequest(() =>
            sellerFlowApiClient.listAdvertiserCreatives(businessRecord.advertiserId, workspaceId),
          );
          creatives = buildCreativeCollection({
            workspace,
            businessRecord,
            items: advertiserCreativeResponse?.items || [],
          });
        }
      } catch (creativeError) {
        console.warn('Falling back to selected workspace creative only', creativeError);
        creatives = buildCreativeCollection({
          workspace,
          businessRecord,
          items: [],
        });
      }

      const fullBusinessProfile = businessProfileResponse
        ? businessProfileFromBackend(businessProfileResponse, {
          id: businessRecord.id,
          workspaceId,
          organizationId: businessRecord.organizationId,
          business: workspace?.business || null,
        })
        : shellBusinessProfile;

      hydrateBusinessProfileForBusiness(businessRecord.id, fullBusinessProfile, {
        setActive,
        existingBusiness: businessRecord,
      });

      const proposals = (proposalsResponse?.items || [])
        .map(proposalFromBackend)
        .sort((a, b) => (b.version || 0) - (a.version || 0));
      const latestProposal = getLatestProposal(proposals);
      const selectedPackage = selectedPackageFromOfferConfig(
        offerResponse?.config || latestProposal?.offer_snapshot_json || {},
      );
      const agreement = agreementResponse ? agreementFromBackend(agreementResponse) : null;
      const reportSummary = workspaceReportSummaryResponse || null;
      const campaign = campaignResponse
        ? campaignFromBackend(campaignResponse, latestReportSnapshot, reportSummary)
        : null;
      const activity = (activityResponse?.items || []).map(activityFromBackend);
      const readiness = readinessResponse ? launchReadinessFromBackend(readinessResponse) : null;

      setCreativesForBusiness(businessRecord.id, creatives);
      setProposalsForBusiness(businessRecord.id, proposals);
      setWorkspaceActivityByBusiness((prev) => ({ ...prev, [businessRecord.id]: activity }));
      setLaunchReadinessByBusiness((prev) => ({ ...prev, [businessRecord.id]: readiness }));
      setCampaignsByBusiness((prev) => ({ ...prev, [businessRecord.id]: campaign ? [campaign] : [] }));
      setReportSummaryByBusiness((prev) => ({ ...prev, [businessRecord.id]: reportSummary }));
      setLatestReportSnapshotByBusiness((prev) => ({ ...prev, [businessRecord.id]: latestReportSnapshot }));
      if (campaign?.id) {
        setReportSnapshotHistoryByCampaign((prev) => ({ ...prev, [campaign.id]: reportSnapshotHistory }));
      }

      if (setActive) {
        setSelectedCreative(workspace.selected_creative_binding_id || null);
        setCreativeSet(
          workspace.selected_creative
            ? creativeSetFromCreative(
              selectedWorkspaceCreativeFromBackend(workspace.selected_creative, {
                businessId: businessRecord.id,
                businessName: businessRecord.name,
              }),
              businessRecord.name,
            )
            : null,
        );
        setSelectedPackage(selectedPackage);
        setProposal(latestProposal);
        setAgreement(agreement);
        setCampaign(campaign);
      }

      return {
        workspace,
        business: businessRecord,
        creatives,
        proposals,
        latestProposal,
        selectedPackage,
        agreement,
        readiness,
        campaign,
        activity,
        reportSummary,
        latestReportSnapshot,
      };
    } catch (error) {
      setWorkspaceErrorState(businessId, error);
      throw error;
    } finally {
      setWorkspaceLoadingState(businessId, false);
    }
  }, [
    optionalRequest,
    setWorkspaceErrorState,
    setWorkspaceLoadingState,
    setCreativesForBusiness,
    setProposalsForBusiness,
        setActiveBusinessId,
    hydrateBusinessProfileForBusiness,
    setBusinessProfile,
    upsertBusiness,
    setSelectedCreative,
    setCreativeSet,
    setSelectedPackage,
    setProposal,
    setAgreement,
    setCampaign,
  ]);

  const loadCreativesForBusiness = useCallback(async (
    businessId,
    { workspace = null, businessRecord = null } = {},
  ) => {
    const resolvedBusiness = businessRecord || businessesRef.current?.[businessId] || null;
    const workspaceId = workspace?.id || resolvedBusiness?.workspaceId || getWorkspaceIdForBusiness(businessId);
    const advertiserId = resolvedBusiness?.advertiserId || businessId;

    if (!workspaceId) return [];

    const workspaceShell = workspace || await optionalRequest(() => sellerFlowApiClient.getWorkspace(workspaceId));
    let creativeItems = [];
    let lastError = null;

    try {
      const workspaceResponse = await optionalRequest(() => sellerFlowApiClient.getWorkspaceCreatives(workspaceId));
      creativeItems = workspaceResponse?.items || [];
    } catch (error) {
      lastError = error;
    }

    if (!creativeItems.length) {
      try {
        const advertiserResponse = await optionalRequest(() =>
          sellerFlowApiClient.listAdvertiserCreatives(advertiserId, workspaceId),
        );
        creativeItems = advertiserResponse?.items || [];
      } catch (error) {
        if (!lastError) lastError = error;
      }
    }

    if (lastError && !creativeItems.length && !workspaceShell?.selected_creative) {
      throw lastError;
    }

    const creatives = buildCreativeCollection({
      workspace: workspaceShell,
      businessRecord: resolvedBusiness,
      items: creativeItems,
    });

    setCreativesForBusiness(businessId, creatives);
    if (workspaceShell?.selected_creative_binding_id) {
      setSelectedCreative(workspaceShell.selected_creative_binding_id);
    }

    return creatives;
  }, [
    getWorkspaceIdForBusiness,
    optionalRequest,
    setCreativesForBusiness,
    setSelectedCreative,
  ]);

  const getWorkspaceCreativeForBusiness = useCallback(async (businessId, bindingId) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    if (!workspaceId || !bindingId) return null;

    const [workspaceResponse, creativeResponse] = await Promise.all([
      optionalRequest(() => sellerFlowApiClient.getWorkspace(workspaceId)),
      sellerFlowApiClient.getWorkspaceCreative(workspaceId, bindingId),
    ]);

    const creative = creativeFromBackend(
      creativeResponse,
      workspaceResponse?.selected_creative_binding_id,
    );
    const existingCreatives = creativesRef.current?.[businessId] || [];
    const selectedBindingId = workspaceResponse?.selected_creative_binding_id || selectedCreativeIdRef.current || null;
    const nextCreatives = syncCreativeSelectionFlags(
      upsertCreativeInCollection(existingCreatives, creative),
      selectedBindingId,
    );

    setCreativesForBusiness(businessId, nextCreatives);
    return creative;
  }, [
    getWorkspaceIdForBusiness,
    optionalRequest,
    setCreativesForBusiness,
  ]);

  const ensureWorkspaceForAdvertiser = useCallback(async (
    advertiserId,
    originJson = null,
    options = {},
  ) => {
    if (!options.lightweight) {
      return loadWorkspaceBundle(advertiserId, { ...options, ensure: true, originJson });
    }

    const workspace = await sellerFlowApiClient.ensureWorkspace(advertiserId, originJson);
    const businessRecord = businessRecordFromWorkspace(workspace);
    const shellBusinessProfile = workspace?.business_profile
      ? businessProfileFromWorkspaceShell(workspace, businessRecord)
      : businessProfileFromBusinessRecord(businessRecord);

    upsertBusiness(businessRecord);
    hydrateBusinessProfileForBusiness(businessRecord.id, shellBusinessProfile, {
      setActive: Boolean(options.setActive),
      existingBusiness: businessRecord,
    });

    if (options.setActive) {
      setActiveBusinessId(businessRecord.id);
      setBusinessProfile(shellBusinessProfile);
    }

    return {
      workspace,
      business: businessRecord,
      businessProfile: shellBusinessProfile,
    };
  }, [
    hydrateBusinessProfileForBusiness,
    loadWorkspaceBundle,
    setActiveBusinessId,
    setBusinessProfile,
    upsertBusiness,
  ]);

  const refreshWorkspaceActivity = useCallback(async (businessId) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    if (!workspaceId) return [];
    const response = await optionalRequest(() => sellerFlowApiClient.getWorkspaceActivity(workspaceId));
    const activity = (response?.items || []).map(activityFromBackend);
    setWorkspaceActivityByBusiness((prev) => ({ ...prev, [businessId]: activity }));
    return activity;
  }, [getWorkspaceIdForBusiness, optionalRequest]);

  const refreshLaunchReadiness = useCallback(async (businessId) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    if (!workspaceId) return null;
    const response = await optionalRequest(() => sellerFlowApiClient.getLaunchReadiness(workspaceId));
    const readiness = response ? launchReadinessFromBackend(response) : null;
    setLaunchReadinessByBusiness((prev) => ({ ...prev, [businessId]: readiness }));
    return readiness;
  }, [getWorkspaceIdForBusiness, optionalRequest]);

  const generateWorkspaceCreativeForBusiness = useCallback(async (businessId, payload) => {
    let workspaceId = getWorkspaceIdForBusiness(businessId);

    if (!workspaceId) {
      const ensured = await loadWorkspaceBundle(businessId, { ensure: true, setActive: true });
      workspaceId = ensured?.workspace?.id || getWorkspaceIdForBusiness(businessId);
    }

    if (!workspaceId) {
      throw new Error('Workspace is required before generating a creative.');
    }

    try {
      const response = await sellerFlowApiClient.generateWorkspaceCreative(workspaceId, payload);
      const businessName = businessesRef.current?.[businessId]?.name || businessProfileRef.current?.name || 'Business';
      const creative = creativeFromBackend(response, selectedCreativeIdRef.current);
      const nextCreatives = upsertCreativeInCollection(creativesRef.current?.[businessId] || [], creative);

      setCreativesForBusiness(businessId, nextCreatives);
      setCreativeSet(creativeSetFromCreative(creative, businessName));
      await refreshWorkspaceActivity(businessId);
      return creative;
    } catch (error) {
      if (error instanceof SellerFlowApiError && error.isBusinessProfileEnrichmentIncomplete) {
        try {
          await refreshBusinessProfileForBusiness(businessId, {
            workspaceId,
            setActive: activeBusinessIdRef.current === businessId,
          });
        } catch (refreshError) {
          console.warn('Could not refresh business-profile state after generation gating', refreshError);
        }
      }
      throw error;
    }
  }, [
    getWorkspaceIdForBusiness,
    loadWorkspaceBundle,
    refreshBusinessProfileForBusiness,
    refreshWorkspaceActivity,
    setCreativesForBusiness,
    setCreativeSet,
  ]);

  const bootstrapPitchKitForBusiness = useCallback(async (businessId, options = {}) => {
    let workspaceId = getWorkspaceIdForBusiness(businessId);

    if (!workspaceId) {
      const ensured = await loadWorkspaceBundle(businessId, { ensure: true, setActive: true });
      workspaceId = ensured?.workspace?.id || getWorkspaceIdForBusiness(businessId);
    }

    if (!workspaceId) {
      throw new Error('Workspace is required before bootstrapping PitchKit.');
    }

    try {
      const response = await sellerFlowApiClient.bootstrapPitchKit(workspaceId, options);
      const bootstrap = pitchBootstrapFromBackend(response);
      const businessName = businessesRef.current?.[businessId]?.name || businessProfileRef.current?.name || 'Business';
      const nextCreatives = upsertCreativeInCollection(
        creativesRef.current?.[businessId] || [],
        bootstrap.creative,
      );

      setCreativesForBusiness(businessId, nextCreatives);
      setCreativeSet(creativeSetFromCreative(bootstrap.creative, businessName));
      await refreshWorkspaceActivity(businessId);
      return bootstrap;
    } catch (error) {
      if (error instanceof SellerFlowApiError && error.isBusinessProfileEnrichmentIncomplete) {
        try {
          await refreshBusinessProfileForBusiness(businessId, {
            workspaceId,
            setActive: activeBusinessIdRef.current === businessId,
          });
        } catch (refreshError) {
          console.warn('Could not refresh business-profile state after PitchKit bootstrap gating', refreshError);
        }
      }
      throw error;
    }
  }, [
    getWorkspaceIdForBusiness,
    loadWorkspaceBundle,
    refreshBusinessProfileForBusiness,
    refreshWorkspaceActivity,
    setCreativesForBusiness,
    setCreativeSet,
  ]);

  const renameCreativeForBusiness = useCallback(async (businessId, creativeOrBindingId, creativeName) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    const bindingId = typeof creativeOrBindingId === 'string'
      ? creativeOrBindingId
      : creativeOrBindingId?.bindingId || creativeOrBindingId?.id || null;
    const nextName = typeof creativeName === 'string' ? creativeName.trim() : '';

    if (!workspaceId || !bindingId) {
      throw new Error('Workspace and creative binding are required to rename a creative.');
    }

    if (!nextName) {
      throw new Error('Creative name cannot be blank.');
    }

    const response = await sellerFlowApiClient.updateWorkspaceCreative(workspaceId, bindingId, {
      creative_name: nextName,
    });
    const creative = creativeFromBackend(response, selectedCreativeIdRef.current);
    const nextCreatives = upsertCreativeInCollection(creativesRef.current?.[businessId] || [], creative);
    const businessName = businessesRef.current?.[businessId]?.name || businessProfileRef.current?.name || 'Business';
    const currentCreativeSetId = creativeSetRef.current?.binding_id || creativeSetRef.current?.id || null;

    setCreativesForBusiness(businessId, nextCreatives);
    if (creative.selectedOnWorkspace) {
      setSelectedCreative(creative.bindingId);
    }
    if (
      creative.selectedOnWorkspace
      || selectedCreativeIdRef.current === bindingId
      || currentCreativeSetId === bindingId
    ) {
      setCreativeSet(creativeSetFromCreative(creative, businessName));
    }
    await refreshWorkspaceActivity(businessId);
    return creative;
  }, [
    getWorkspaceIdForBusiness,
    refreshWorkspaceActivity,
    setCreativesForBusiness,
    setCreativeSet,
    setSelectedCreative,
  ]);

  const deleteCreativeForBusiness = useCallback(async (businessId, creativeOrBindingId) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    const bindingId = typeof creativeOrBindingId === 'string'
      ? creativeOrBindingId
      : creativeOrBindingId?.bindingId || creativeOrBindingId?.id || null;
    const currentCreativeSetId = creativeSetRef.current?.binding_id || creativeSetRef.current?.id || null;

    if (!workspaceId || !bindingId) {
      throw new Error('Workspace and creative binding are required to delete a creative.');
    }

    await sellerFlowApiClient.deleteWorkspaceCreative(workspaceId, bindingId);

    const nextCreatives = sortCreativeCollection(
      (creativesRef.current?.[businessId] || []).filter((creative) =>
        creative.bindingId !== bindingId && creative.id !== bindingId
      ),
    );

    setCreativesForBusiness(businessId, nextCreatives);

    if (selectedCreativeIdRef.current === bindingId) {
      setSelectedCreative(null);
    }

    if (currentCreativeSetId === bindingId) {
      setCreativeSet(null);
    }

    await refreshWorkspaceActivity(businessId);
    return true;
  }, [
    getWorkspaceIdForBusiness,
    refreshWorkspaceActivity,
    setCreativesForBusiness,
    setCreativeSet,
    setSelectedCreative,
  ]);

  const retryWorkspaceCreativeForBusiness = useCallback(async (businessId, bindingId) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);

    if (!workspaceId || !bindingId) {
      throw new Error('Workspace and creative binding are required before retrying generation.');
    }

    const response = await sellerFlowApiClient.retryWorkspaceCreative(workspaceId, bindingId);
    const creative = creativeFromBackend(response, selectedCreativeIdRef.current);
    const nextCreatives = upsertCreativeInCollection(creativesRef.current?.[businessId] || [], creative);
    const businessName = businessesRef.current?.[businessId]?.name || businessProfileRef.current?.name || 'Business';

    setCreativesForBusiness(businessId, nextCreatives);
    setCreativeSet(creativeSetFromCreative(creative, businessName));
    await refreshWorkspaceActivity(businessId);
    return creative;
  }, [
    getWorkspaceIdForBusiness,
    refreshWorkspaceActivity,
    setCreativesForBusiness,
    setCreativeSet,
  ]);

  const selectCreativeForBusiness = useCallback(async (businessId, creativeOrBindingId) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    const bindingId = typeof creativeOrBindingId === 'string'
      ? creativeOrBindingId
      : creativeOrBindingId?.bindingId || creativeOrBindingId?.id || null;

    if (!workspaceId || !bindingId) {
      throw new Error('Workspace and creative binding are required to select creative.');
    }

    const workspaceResponse = await sellerFlowApiClient.selectWorkspaceCreative(workspaceId, bindingId);
    const nextSelectedBindingId = workspaceResponse?.selected_creative_binding_id || bindingId;
    const selectedFallback = selectedWorkspaceCreativeFromBackend(workspaceResponse?.selected_creative, {
      businessId,
      businessName: businessesRef.current?.[businessId]?.name || businessProfileRef.current?.name || 'Business',
    });
    const existingCreatives = creativesRef.current?.[businessId] || [];
    const baseCreatives = selectedFallback
      ? upsertCreativeInCollection(existingCreatives, selectedFallback)
      : existingCreatives;

    setSelectedCreative(nextSelectedBindingId);
    setCreativesForBusiness(
      businessId,
      syncCreativeSelectionFlags(baseCreatives, nextSelectedBindingId),
    );
    if (selectedFallback) {
      setCreativeSet(
        creativeSetFromCreative(
          selectedFallback,
          businessesRef.current?.[businessId]?.name || businessProfileRef.current?.name || 'Business',
        ),
      );
    }
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return nextSelectedBindingId;
  }, [
    getWorkspaceIdForBusiness,
    refreshLaunchReadiness,
    refreshWorkspaceActivity,
    setCreativesForBusiness,
    setSelectedCreative,
    setCreativeSet,
  ]);

  const saveOfferConfigForBusiness = useCallback(async (businessId, selectedPackage) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    if (!workspaceId) {
      throw new Error('Workspace is required before saving an offer.');
    }

    const response = await sellerFlowApiClient.upsertOfferConfig(
      workspaceId,
      offerConfigFromSelectedPackage(selectedPackage),
    );
    const nextSelectedPackage = selectedPackageFromOfferConfig(response?.config || {});
    setSelectedPackage(nextSelectedPackage);
    await refreshLaunchReadiness(businessId);
    return nextSelectedPackage;
  }, [getWorkspaceIdForBusiness, refreshLaunchReadiness, setSelectedPackage]);

  const ensureDraftProposalForBusiness = useCallback(async (businessId, payload = {}) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    if (!workspaceId) {
      throw new Error('Workspace is required before creating a proposal.');
    }

    const currentProposals = proposalsRef.current?.[businessId] || [];
    const latestProposal = getLatestProposal(currentProposals);
    if (latestProposal && ['draft', 'changes_requested'].includes(latestProposal.status)) {
      setProposal(latestProposal);
      return latestProposal;
    }

    const response = await sellerFlowApiClient.createProposal(workspaceId, payload);
    const nextProposal = proposalFromBackend(response);
    upsertProposalToBusiness(businessId, nextProposal);
    setProposal(nextProposal);
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return nextProposal;
  }, [getWorkspaceIdForBusiness, refreshLaunchReadiness, refreshWorkspaceActivity, setProposal, upsertProposalToBusiness]);

  const patchProposalForBusiness = useCallback(async (businessId, proposal, selectedPackage = state.selectedPackage) => {
    const response = await sellerFlowApiClient.patchProposal(
      proposal.id,
      proposalPatchFromUi(proposal, selectedPackage),
    );
    const nextProposal = proposalFromBackend(response);
    upsertProposalToBusiness(businessId, nextProposal);
    setProposal(nextProposal);
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return nextProposal;
  }, [refreshLaunchReadiness, refreshWorkspaceActivity, setProposal, state.selectedPackage, upsertProposalToBusiness]);

  const shareProposalForBusiness = useCallback(async (businessId, proposalOrId, payload = {}) => {
    const proposalId = typeof proposalOrId === 'string' ? proposalOrId : proposalOrId?.id;
    if (!proposalId) {
      throw new Error('Proposal is required before sharing.');
    }

    const response = await sellerFlowApiClient.shareProposal(proposalId, payload);
    const nextProposal = proposalFromBackend(response);
    upsertProposalToBusiness(businessId, nextProposal);
    setProposal(nextProposal);
    await refreshWorkspaceActivity(businessId);
    return nextProposal;
  }, [refreshWorkspaceActivity, setProposal, upsertProposalToBusiness]);

  const sendProposalForBusiness = useCallback(async (businessId, proposal, selectedPackage = state.selectedPackage) => {
    const patchedProposal = proposal?.id
      ? await patchProposalForBusiness(businessId, proposal, selectedPackage)
      : await ensureDraftProposalForBusiness(businessId, {
        package_name: proposal?.packageName || selectedPackage?.name || null,
        pitch_message: proposal?.pitch_message || '',
        delivery_method: proposal?.deliveryMethod || 'link',
        contact_json: proposal?.contact || null,
        follow_up_days: proposal?.followUpDays ?? null,
      });
    const response = await sellerFlowApiClient.sendProposal(patchedProposal.id, {
      recipient_email: patchedProposal.recipientEmail || patchedProposal.contact?.email || null,
    });
    const nextProposal = proposalFromBackend(response);
    upsertProposalToBusiness(businessId, nextProposal);
    setProposal(nextProposal);
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return nextProposal;
  }, [
    ensureDraftProposalForBusiness,
    patchProposalForBusiness,
    refreshLaunchReadiness,
    refreshWorkspaceActivity,
    setProposal,
    state.selectedPackage,
    upsertProposalToBusiness,
  ]);

  const loadPublicProposal = useCallback(async (token) => {
    try {
      const response = await sellerFlowApiClient.getPublicProposal(token);
      return publicProposalFromBackend(response);
    } catch (error) {
      if (error instanceof SellerFlowApiError && error.publicProposalState) {
        return publicProposalErrorStateFromApi(error);
      }
      throw error;
    }
  }, []);

  const recordProposalResponseForBusiness = useCallback(async (businessId, proposalId, status, detail = null) => {
    const response = await sellerFlowApiClient.recordProposalResponse(proposalId, { status, detail });
    const nextProposal = proposalFromBackend(response);
    upsertProposalToBusiness(businessId, nextProposal);
    setProposal(nextProposal);
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return nextProposal;
  }, [refreshLaunchReadiness, refreshWorkspaceActivity, setProposal, upsertProposalToBusiness]);

  const createProposalRevisionForBusiness = useCallback(async (businessId, proposalId, detail = null) => {
    const response = await sellerFlowApiClient.createProposalRevision(proposalId, detail);
    const nextProposal = proposalFromBackend(response);
    upsertProposalToBusiness(businessId, nextProposal);
    setProposal(nextProposal);
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return nextProposal;
  }, [refreshLaunchReadiness, refreshWorkspaceActivity, setProposal, upsertProposalToBusiness]);

  const loadWorkspaceAgreementForBusiness = useCallback(async (businessId) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    if (!workspaceId) return null;
    const response = await optionalRequest(() => sellerFlowApiClient.getWorkspaceAgreement(workspaceId));
    const agreement = response ? agreementFromBackend(response) : null;
    setAgreement(agreement);
    await refreshLaunchReadiness(businessId);
    return agreement;
  }, [getWorkspaceIdForBusiness, optionalRequest, refreshLaunchReadiness, setAgreement]);

  const createAgreementForProposal = useCallback(async (businessId, proposalId) => {
    const response = await sellerFlowApiClient.createAgreementForProposal(proposalId);
    const agreement = agreementFromBackend(response);
    setAgreement(agreement);
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return agreement;
  }, [refreshLaunchReadiness, refreshWorkspaceActivity, setAgreement]);

  const startBillingForAgreement = useCallback(async (businessId, agreementId, billingMode, metadata = null) => {
    const response = await sellerFlowApiClient.startBilling(agreementId, {
      billing_provider: 'stripe_mock',
      billing_mode: billingMode,
      metadata_json: metadata || undefined,
    });
    const agreement = agreementFromBackend(response);
    setAgreement(agreement);
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return agreement;
  }, [refreshLaunchReadiness, refreshWorkspaceActivity, setAgreement]);

  const confirmBillingForAgreement = useCallback(async (businessId, agreementId, payload = {}) => {
    const response = await sellerFlowApiClient.confirmBilling(agreementId, payload);
    const agreement = agreementFromBackend(response);
    setAgreement(agreement);

    if (agreement.proposalId) {
      const proposalResponse = await optionalRequest(() => sellerFlowApiClient.getProposal(agreement.proposalId));
      if (proposalResponse) {
        const nextProposal = proposalFromBackend(proposalResponse);
        upsertProposalToBusiness(businessId, nextProposal);
        setProposal(nextProposal);
      }
    }

    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return agreement;
  }, [optionalRequest, refreshLaunchReadiness, refreshWorkspaceActivity, setAgreement, setProposal, upsertProposalToBusiness]);

  const loadCampaignForBusiness = useCallback(async (businessId) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    if (!workspaceId) return null;

    const campaignResponse = await optionalRequest(() => sellerFlowApiClient.getWorkspaceCampaign(workspaceId));
    if (!campaignResponse) {
      setCampaign(null);
      setCampaignsByBusiness((prev) => ({ ...prev, [businessId]: [] }));
      return null;
    }

    const [reportSummary, latestSnapshot, snapshotHistory] = await Promise.all([
      optionalRequest(() => sellerFlowApiClient.getWorkspaceCampaignReportSummary(workspaceId)),
      optionalRequest(() => sellerFlowApiClient.getLatestReportSnapshot(campaignResponse.id)),
      optionalRequest(() => sellerFlowApiClient.listReportSnapshots(campaignResponse.id)),
    ]);

    const campaign = campaignFromBackend(campaignResponse, latestSnapshot, reportSummary);
    setCampaign(campaign);
    setCampaignsByBusiness((prev) => ({ ...prev, [businessId]: [campaign] }));
    setReportSummaryByBusiness((prev) => ({ ...prev, [businessId]: reportSummary || null }));
    setLatestReportSnapshotByBusiness((prev) => ({ ...prev, [businessId]: latestSnapshot || null }));
    setReportSnapshotHistoryByCampaign((prev) => ({ ...prev, [campaign.id]: snapshotHistory?.items || [] }));
    return campaign;
  }, [getWorkspaceIdForBusiness, optionalRequest, setCampaign]);

  const launchCampaignForBusiness = useCallback(async (businessId, payload = {}) => {
    const workspaceId = getWorkspaceIdForBusiness(businessId);
    if (!workspaceId) {
      throw new Error('Workspace is required before launch.');
    }

    const response = await sellerFlowApiClient.launchCampaign(workspaceId, {
      provider: 'clearline_mock',
      ...payload,
    });
    const campaign = campaignFromBackend(response, null, null);
    setCampaign(campaign);
    setCampaignsByBusiness((prev) => ({ ...prev, [businessId]: [campaign] }));
    await refreshLaunchReadiness(businessId);
    await refreshWorkspaceActivity(businessId);
    return loadCampaignForBusiness(businessId);
  }, [getWorkspaceIdForBusiness, loadCampaignForBusiness, refreshLaunchReadiness, refreshWorkspaceActivity, setCampaign]);

  const value = {
    ...state,
    sellerHomeSummary,
    sellerHomeQueue,
    sellerHomeLoading,
    workspaceActivityByBusiness,
    launchReadinessByBusiness,
    campaignsByBusiness,
    reportSummaryByBusiness,
    latestReportSnapshotByBusiness,
    reportSnapshotHistoryByCampaign,
    businessProfilesByBusiness,
    businessProfileLoadingByBusiness,
    businessProfileErrorByBusiness,
    workspaceLoadingByBusiness,
    workspaceErrorByBusiness,
    setDealId,
    setLeadId,
    setLead,
    setCurrentStage,
    setBusinessProfile,
    setScope,
    setCreativeSet,
    setSelectedPackage,
    setProposal,
    setSendConfig,
    setAgreement,
    setCampaign,
    setReturnTo,
    reset,
    // V2
    addBusiness,
    upsertBusiness,
    setActiveBusinessId,
    addCreative,
    setCreativesForBusiness,
    updateCreativeStatus,
    addProposalToBusiness,
    setProposalsForBusiness,
    upsertProposalToBusiness,
    updateProposalStatus,
    updateCreative,
    updateProposal,
    setSelectedCreative,
    updateAgreement,
    linkProposalToCreative,
    getWorkspaceIdForBusiness,
    loadSellerHome,
    loadWorkspaceBundle,
    ensureWorkspaceForAdvertiser,
    loadBusinessProfileForBusiness,
    refreshBusinessProfileForBusiness,
    enrichBusinessProfileForBusiness,
    refreshWorkspaceActivity,
    refreshLaunchReadiness,
    loadCreativesForBusiness,
    getWorkspaceCreativeForBusiness,
    bootstrapPitchKitForBusiness,
    renameCreativeForBusiness,
    deleteCreativeForBusiness,
    generateWorkspaceCreativeForBusiness,
    retryWorkspaceCreativeForBusiness,
    selectCreativeForBusiness,
    saveOfferConfigForBusiness,
    ensureDraftProposalForBusiness,
    patchProposalForBusiness,
    shareProposalForBusiness,
    sendProposalForBusiness,
    loadPublicProposal,
    recordProposalResponseForBusiness,
    createProposalRevisionForBusiness,
    loadWorkspaceAgreementForBusiness,
    createAgreementForProposal,
    startBillingForAgreement,
    confirmBillingForAgreement,
    launchCampaignForBusiness,
    loadCampaignForBusiness,
  };

  return (
    <DealFlowContext.Provider value={value}>
      {children}
    </DealFlowContext.Provider>
  );
}

/**
 * Hook to access the deal flow context.
 * Must be used within <DealFlowProvider>.
 */
export default function useDealFlow() {
  const context = useContext(DealFlowContext);
  if (!context) {
    throw new Error('useDealFlow must be used within a DealFlowProvider');
  }
  return context;
}
